import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { adminApi, apiError } from "../adminApi";
import { Panel, Stat } from "../components/Bits";

export default function AdminPeo() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    adminApi
      .get("/crm/peo/summary")
      .then(({ data: d }) => setData(d))
      .catch((e) => setError(apiError(e)));
  }, []);

  if (error) return <p className="text-sm text-terra" data-testid="peo-error">{error}</p>;
  if (!data) return <p className="text-sm text-cream/40" data-testid="peo-loading">Loading…</p>;

  const { counts, named_peos: named, top_cities: cities } = data;
  const onPeo = counts.confirmed + counts.likely;

  return (
    <div className="space-y-7" data-testid="admin-peo-page">
      <div>
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-terra">PEO signal</p>
        <h1 className="mt-3 font-serif text-4xl font-light leading-none text-cream">
          Who is already on a PEO
        </h1>
        <p className="mt-3 max-w-3xl text-sm leading-relaxed text-cream/50">
          Built from public filings only. A <span className="text-gold">confirmed</span> signal means a
          PEO is named on the employer&rsquo;s own Form 5500. A <span className="text-sage">likely</span>{" "}
          signal means the employer files a retirement plan but no health plan of any size — the coverage
          is being administered somewhere else, most often a PEO or a multiple employer welfare
          arrangement. There is no public index of PEO clients, so treat &ldquo;likely&rdquo; as a strong
          lead, not a fact.
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Stat label="On a PEO" value={onPeo.toLocaleString()} hint="Confirmed plus likely"
              testid="peo-stat-onpeo" />
        <Stat label="Confirmed" value={counts.confirmed.toLocaleString()}
              hint="PEO named on the filing" testid="peo-stat-confirmed" />
        <Stat label="Likely" value={counts.likely.toLocaleString()}
              hint="Retirement filing, no health filing" testid="peo-stat-likely" />
        <Stat label="No signal" value={counts.unknown.toLocaleString()}
              hint="Files their own health plan" testid="peo-stat-unknown" />
      </div>

      <div className="grid gap-5 lg:grid-cols-2">
        <Panel title="Named incumbents" testid="panel-named-peos">
          {named.length === 0 ? (
            <p className="text-sm leading-relaxed text-cream/45">
              No PEO names captured yet. Names arrive two ways: a PEO appears on a filing, or you confirm
              one on an account after a conversation. Every name you confirm here builds the map.
            </p>
          ) : (
            <ul className="divide-y divide-cream/10">
              {named.map((row) => (
                <li key={row.peo} className="flex items-center justify-between gap-4 py-3">
                  <Link to="/admin/accounts"
                        className="text-sm text-cream transition-colors duration-200 hover:text-gold">
                    {row.peo}
                  </Link>
                  <span className="font-mono text-xs text-cream/60">
                    {row.accounts} accounts · {row.lives.toLocaleString()} lives
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Panel>

        <Panel title="Where they are" testid="panel-peo-cities">
          <ul className="divide-y divide-cream/10">
            {cities.map((row) => (
              <li key={row.city || "unknown"} className="flex items-center justify-between py-2.5 text-sm">
                <span className="text-cream/70">{row.city || "Unknown city"}</span>
                <span className="font-mono text-xs text-cream/60">{row.accounts}</span>
              </li>
            ))}
          </ul>
          <Link to="/admin/accounts"
                className="mt-5 inline-block font-mono text-[10px] uppercase tracking-[0.25em] text-gold transition-colors duration-200 hover:text-cream"
                data-testid="peo-view-accounts">
            Filter accounts by PEO status →
          </Link>
        </Panel>
      </div>
    </div>
  );
}
